import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConversationModel, InboxEvent, MessageModel } from '@app/interfaces/messaging';

export interface InboxState {
  conversations: Record<number, ConversationModel>;
  /** Thứ tự hiển thị danh sách (mới nhắn lên đầu). */
  order: number[];
  messages: Record<number, MessageModel[]>;
  hasMore: Record<number, boolean>;
  unreadTotal: number;
  selectedId: number | null;
}

export const initialInboxState: InboxState = {
  conversations: {},
  order: [],
  messages: {},
  hasMore: {},
  unreadTotal: 0,
  selectedId: null,
};

const timeOf = (c: ConversationModel): number => (c.last_message_at ? Date.parse(c.last_message_at) : 0);

const sortOrder = (state: InboxState): number[] =>
  [...state.order].sort((a, b) => timeOf(state.conversations[b]) - timeOf(state.conversations[a]) || b - a);

// Trạng thái chỉ tiến lên: sự kiện `sent`/`read` qua cable có thể tới TRƯỚC response 201
// (`pending`) của chính request gửi. `failed` thay được mọi trạng thái (và ngược lại khi gửi lại).
const STATUS_RANK: Record<MessageModel['status'], number> = {
  pending: 0,
  sending: 1,
  sent: 2,
  delivered: 3,
  read: 4,
  failed: -1,
};

const newerStatus = (current: MessageModel, incoming: MessageModel): MessageModel['status'] => {
  if (incoming.status === 'failed' || current.status === 'failed') return incoming.status;
  return STATUS_RANK[incoming.status] >= STATUS_RANK[current.status] ? incoming.status : current.status;
};

const byTime = (a: MessageModel, b: MessageModel): number =>
  Date.parse(a.created_at) - Date.parse(b.created_at) || a.id - b.id;

/**
 * Tin trùng (cùng id, hoặc bản lạc quan cùng client_uuid) thì thay, không thêm; tin mới
 * chèn đúng vị trí theo thời gian (job webhook song song có thể đến lệch thứ tự).
 */
export const mergeMessage = (list: MessageModel[], message: MessageModel): MessageModel[] => {
  const index = list.findIndex(
    (m) => m.id === message.id || (message.client_uuid !== null && m.client_uuid === message.client_uuid),
  );
  if (index === -1) return [...list, message].sort(byTime);
  const current = list[index];
  const merged = { ...message, status: newerStatus(current, message) };
  return list.map((m, i) => (i === index ? merged : m));
};

const withConversation = (state: InboxState, conversation: ConversationModel): InboxState => {
  const previous = state.conversations[conversation.id];
  // Đang mở hội thoại thì tin mới coi như đã đọc (UI gọi mark_read sau).
  const merged =
    state.selectedId === conversation.id ? { ...conversation, unread_count: 0 } : conversation;
  const unreadDelta = (merged.unread_count > 0 ? 1 : 0) - (previous && previous.unread_count > 0 ? 1 : 0);
  const next: InboxState = {
    ...state,
    conversations: { ...state.conversations, [conversation.id]: merged },
    order: previous ? state.order : [...state.order, conversation.id],
    unreadTotal: Math.max(0, state.unreadTotal + unreadDelta),
  };
  return { ...next, order: sortOrder(next) };
};

/** Gỡ hội thoại khỏi danh sách (giao cho người khác mà mình không có quyền xem tất cả). */
const withoutConversation = (state: InboxState, id: number): InboxState => {
  if (!state.conversations[id]) return state;
  const { [id]: removed, ...conversations } = state.conversations;
  const { [id]: _messages, ...messages } = state.messages;
  return {
    ...state,
    conversations,
    messages,
    order: state.order.filter((cid) => cid !== id),
    unreadTotal: Math.max(0, state.unreadTotal - (removed.unread_count > 0 ? 1 : 0)),
  };
};

const withMessage = (state: InboxState, message: MessageModel): InboxState => {
  const list = state.messages[message.conversation_id];
  if (!list) return state; // chưa tải lịch sử hội thoại này — mở ra sẽ tải đủ
  return { ...state, messages: { ...state.messages, [message.conversation_id]: mergeMessage(list, message) } };
};

/** Áp một sự kiện realtime vào state (thuần, không side effect). */
export const applyInboxEvent = (state: InboxState, event: InboxEvent): InboxState => {
  switch (event.type) {
    case 'message.created':
      return withMessage(withConversation(state, event.conversation), event.message);
    case 'message.updated':
      return withMessage(state, event.message);
    case 'conversation.updated':
      return withConversation(state, event.conversation);
    case 'message.receipts': {
      const list = state.messages[event.conversation_id];
      if (!list) return state;
      const ids = new Set(event.message_ids);
      const updated = list.map((m) => (ids.has(m.id) ? { ...m, status: event.status } : m));
      return { ...state, messages: { ...state.messages, [event.conversation_id]: updated } };
    }
    default:
      return state;
  }
};

const inboxSlice = createSlice({
  name: 'inbox',
  initialState: initialInboxState,
  reducers: {
    setConversations: (state, action: PayloadAction<{ rows: ConversationModel[]; append: boolean }>) => {
      const base: InboxState = action.payload.append ? state : { ...state, conversations: {}, order: [] };
      return action.payload.rows.reduce(withConversationKeepUnread, base);
    },
    setUnreadTotal: (state, action: PayloadAction<number>) => ({ ...state, unreadTotal: action.payload }),
    upsertConversation: (state, action: PayloadAction<ConversationModel>) => withConversation(state, action.payload),
    selectConversation: (state, action: PayloadAction<number | null>) => ({ ...state, selectedId: action.payload }),
    /**
     * replace: mở hội thoại lần đầu. prepend: "Xem tin cũ hơn". merge: tải lại sau khi mất
     * kết nối — giữ lịch sử cũ đã tải và tin lạc quan đang gửi.
     */
    setMessages: (
      state,
      action: PayloadAction<{
        conversationId: number;
        rows: MessageModel[];
        hasMore: boolean;
        mode: 'replace' | 'prepend' | 'merge';
      }>,
    ) => {
      const { conversationId, rows, hasMore, mode } = action.payload;
      const current = state.messages[conversationId] ?? [];
      const list = mode === 'replace' ? rows : rows.reduce(mergeMessage, current);
      return {
        ...state,
        messages: { ...state.messages, [conversationId]: list },
        hasMore: mode === 'merge' ? state.hasMore : { ...state.hasMore, [conversationId]: hasMore },
      };
    },
    upsertMessage: (state, action: PayloadAction<MessageModel>) => withMessage(state, action.payload),
    /** Bỏ tin lạc quan khi API từ chối (chưa có bản ghi ở server). */
    removeMessage: (state, action: PayloadAction<{ conversationId: number; clientUuid: string }>) => {
      const { conversationId, clientUuid } = action.payload;
      const list = state.messages[conversationId];
      if (!list) return state;
      return {
        ...state,
        messages: { ...state.messages, [conversationId]: list.filter((m) => m.client_uuid !== clientUuid) },
      };
    },
    applyEvent: (state, action: PayloadAction<InboxEvent>) => applyInboxEvent(state, action.payload),
    removeConversation: (state, action: PayloadAction<number>) => withoutConversation(state, action.payload),
    /** Đăng xuất: không để người dùng sau thấy hội thoại của người trước (cùng tab). */
    resetInbox: () => initialInboxState,
  },
});

/** Nạp danh sách từ API: giữ unreadTotal do server tính (không cộng dồn lại). */
function withConversationKeepUnread(state: InboxState, conversation: ConversationModel): InboxState {
  const next = withConversation(state, conversation);
  return { ...next, unreadTotal: state.unreadTotal };
}

export const {
  setConversations,
  setUnreadTotal,
  upsertConversation,
  selectConversation,
  setMessages,
  upsertMessage,
  removeMessage,
  applyEvent,
  removeConversation,
  resetInbox,
} = inboxSlice.actions;

export default inboxSlice.reducer;
